import dayjs from 'dayjs';
import config from '../config';

const esc = (v) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

export const formatMoney = (amount, currency) =>
    currency === 'IQD'
        ? `IQD ${Number(amount || 0).toLocaleString()}`
        : `$${Number(amount || 0).toFixed(2)}`;

export const getIncomeName = (record) =>
    record.student ? `${record.student.firstName} ${record.student.lastName}` : '-';

// Sum amounts by currency: { USD: n, IQD: n }
export const sumByCurrency = (records) =>
    records.reduce((acc, r) => {
        const cur = r.currency || 'USD';
        acc[cur] = (acc[cur] || 0) + (r.amount || 0);
        return acc;
    }, {});

// Group records by name: [{ name, count, totals: { USD, IQD } }]
export const groupByName = (records) => {
    const map = new Map();
    records.forEach((r) => {
        const name = getIncomeName(r);
        if (!map.has(name)) map.set(name, { name, count: 0, records: [] });
        const g = map.get(name);
        g.count += 1;
        g.records.push(r);
    });
    return Array.from(map.values())
        .map((g) => ({ name: g.name, count: g.count, totals: sumByCurrency(g.records) }))
        .sort((a, b) => a.name.localeCompare(b.name));
};

const totalsText = (totals) =>
    Object.entries(totals).map(([cur, val]) => formatMoney(val, cur)).join(' + ') || '-';

export const printIncomeReport = ({ records, user, t, language = 'en', title, dateRange }) => {
    const isRTL = language === 'ar' || language === 'ku';
    const logoUrl = user?.academy?.logo ? `${config.baseUrl}${user.academy.logo}` : '';
    const groups = groupByName(records);
    const totals = sumByCurrency(records);

    const rows = records.map((r, i) => `
        <tr>
            <td>${i + 1}</td>
            <td>${dayjs(r.date).format('YYYY-MM-DD')}</td>
            <td>${esc(r.receiptNumber)}</td>
            <td>${esc(getIncomeName(r))}</td>
            <td>${esc(r.category === 'other' && r.description ? r.description : r.category)}</td>
            <td>${esc(r.description)}</td>
            <td>${esc(formatMoney(r.amount, r.currency))}</td>
        </tr>`).join('');

    const groupRows = groups.map((g) => `
        <tr><td>${esc(g.name)}</td><td>${g.count}</td><td>${esc(totalsText(g.totals))}</td></tr>`).join('');

    const html = `<!DOCTYPE html>
<html dir="${isRTL ? 'rtl' : 'ltr'}"><head><meta charset="UTF-8"><title>${esc(title)}</title>
<style>
@font-face { font-family: 'Sped'; src: url('${window.location.origin}/Speda.ttf') format('truetype'); }
* { box-sizing: border-box; }
body { font-family: 'Sped','Inter','Segoe UI',sans-serif; margin: 20px; color: #222; direction: ${isRTL ? 'rtl' : 'ltr'}; }
.head { display:flex; align-items:center; gap:16px; border-bottom:2px solid #333; padding-bottom:10px; margin-bottom:14px; }
.head img { height:60px; }
h1 { font-size:20px; margin:0; } h2 { font-size:15px; margin:0 0 8px; }
.by-name { page-break-before: always; break-before: page; padding-top: 4px; }
.meta { font-size:12px; color:#555; }
table { width:100%; border-collapse:collapse; font-size:12px; }
th, td { border:1px solid #bbb; padding:5px 7px; text-align:${isRTL ? 'right' : 'left'}; }
th { background:#eee; }
tfoot td { font-weight:bold; background:#f6f6f6; }
@media print { @page { size: A4; margin: 12mm; } }
</style></head><body>
<div class="head">
  ${logoUrl ? `<img src="${logoUrl}" alt="" />` : ''}
  <div><h1>${esc(title)}</h1>
  <div class="meta">${esc(user?.academy?.name || '')}${dateRange ? ` · ${esc(dateRange)}` : ''} · ${dayjs().format('YYYY-MM-DD HH:mm')}</div></div>
</div>
<table>
<thead><tr><th>#</th><th>${esc(t('date'))}</th><th>${esc(t('receiptNo'))}</th><th>${esc(t('student'))}</th><th>${esc(t('category'))}</th><th>${esc(t('description'))}</th><th>${esc(t('amount'))}</th></tr></thead>
<tbody>${rows}</tbody>
<tfoot><tr><td colspan="6">${esc(t('incomeGrandTotal'))} (${records.length})</td><td>${esc(totalsText(totals))}</td></tr></tfoot>
</table>
<div class="by-name">
<h2>${esc(t('incomeTotalsByName'))}</h2>
<table>
<thead><tr><th>${esc(t('student'))}</th><th>${esc(t('incomeRecordsCount'))}</th><th>${esc(t('amount'))}</th></tr></thead>
<tbody>${groupRows}</tbody>
</table>
</div>
<script>window.onload = function(){ setTimeout(function(){ window.print(); }, 400); };</script>
</body></html>`;

    const win = window.open('', '_blank');
    if (!win) return false;
    win.document.write(html);
    win.document.close();
    return true;
};
